import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import * as styles from './index.css';

/**
 * Per-character delay. Slow enough to read as typing rather than a flicker; a
 * typical title lands in roughly half a second.
 */
const CHARACTER_MS = 30;

type SpellOutTextProps = {
  text: string;
};

/**
 * Types the text out one character at a time whenever it changes. Conversation
 * titles arrive after auto-naming, so a chip's label swaps under the user. This
 * makes the swap legible instead of a silent replacement.
 *
 * The final text is also rendered invisibly underneath to hold the width, so
 * neither the chip nor the row beside it reflows while the characters land.
 * First paint is not animated: only a change spells out. A title too long for
 * the label fades out at its edge; one that fits is left alone.
 */
export function SpellOutText({ text }: SpellOutTextProps) {
  const [displayed, setDisplayed] = useState(text);
  const settledText = useRef(text);
  const [isTruncated, setIsTruncated] = useState(false);
  const visibleRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (text === settledText.current) return;
    settledText.current = text;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setDisplayed(text);
      return;
    }

    setDisplayed('');
    let count = 0;
    const timer = setInterval(() => {
      count += 1;
      setDisplayed(text.slice(0, count));
      if (count >= text.length) clearInterval(timer);
    }, CHARACTER_MS);

    // Cleared on unmount and on any further change, so a stale timer can't type
    // an older title over a newer one.
    return () => clearInterval(timer);
  }, [text]);

  // Measured only once the typing has settled, so a partially typed title can't
  // read as "fits". The observer catches the chip being resized around it.
  useEffect(() => {
    const element = visibleRef.current;
    if (element == null || displayed !== text) return;
    const sync = () => {
      const truncated = element.scrollWidth > element.clientWidth + 1;
      setIsTruncated((previous) => (previous === truncated ? previous : truncated));
    };
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(element);
    return () => observer.disconnect();
  }, [text, displayed]);

  return (
    <span className={styles.spellOut}>
      {/* The sizer draws the text from a data attribute via CSS `content`, so
          the DOM carries the title once: text queries and the accessibility
          tree only ever see the visible copy. */}
      <span className={styles.spellOutSizer} data-text={text} aria-hidden />
      <span ref={visibleRef} className={clsx(styles.spellOutVisible, isTruncated && styles.spellOutFaded)}>
        {displayed}
      </span>
    </span>
  );
}
